import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Button, Grid, Stack } from "@mui/material";
import { PersonOutline as Person } from "@mui/icons-material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { IconBtnEditBase } from "../../common/utils/icons";
import { useAuth } from "../../contexts/AuthContext";
import { getUserById, myProfile, updateUser } from "../../services/userService";
import { useToast } from "../../services/utils";
import FlexBetween from "../../components/FlexBetween";
import Loader from "../../components/Molecules/Loader/Loader";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useAppTranslation } from "../../hooks/useAppTranslation";

interface UserProfile {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  username: string;
  user_auth_type: string;
  third_party_user_id: string | null;
  is_active: boolean;
  created_on: string;
  updated_on: string;
  last_login: string | null;
}

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo, setUserInfo } = useAuth();
  const { showToast } = useToast();
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const { t } = useAppTranslation();
  const [profile, setProfile] = useState<any>({});
  const [editedProfile, setEditedProfile] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCurrentBreadcrumbPage(t("profile.breadcrumb"));
  }, [setCurrentBreadcrumbPage, t]);

  useEffect(() => {
    fetchUserProfile();
  }, [userInfo]); // Depend on userInfo to refetch when user data is available

  // eslint-disable-next-line react-hooks/exhaustive-deps

  const fetchUserProfile = async () => {
    if (!userInfo?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      // const response = await getUserById(userInfo.id);
      const response = await myProfile();
      setProfile(response);
      setEditedProfile(response);
    } catch (err: any) {
      console.error("Error fetching user profile:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedProfile(profile);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!editedProfile || !profile) return;

    try {
      setIsSaving(true);

      // Prepare payload with only the fields that can be updated
      const payload = {
        firstname: editedProfile.firstname,
        lastname: editedProfile.lastname,
        email: editedProfile.email,
        user_auth_type: editedProfile.user_auth_type,
        third_party_user_id: editedProfile.third_party_user_id,
      };

      const response = await updateUser(profile.id, payload);
      setProfile(response);
      setEditedProfile(response);
      setIsEditing(false);
      showToast(t("profile.toast.updated"), "success");

      // Update localStorage userInfo to reflect changes in navbar
      const updatedUserInfo = {
        ...userInfo,
        firstname: response.firstname,
        lastname: response.lastname,
        email: response.email,
      };
      localStorage.setItem("userInfo", JSON.stringify(updatedUserInfo));

      // Update AuthContext userInfo to immediately reflect changes in navbar
      setUserInfo(updatedUserInfo);
    } catch (err: any) {
      showToast(t("profile.toast.failedUpdate"), "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field: keyof UserProfile, value: string) => {
    if (editedProfile) {
      setEditedProfile({
        ...editedProfile,
        [field]: value,
      });
    }
  };

  const getInitials = (firstname: string, lastname: string) => {
    return `${firstname?.charAt(0) || ""}${
      lastname?.charAt(0) || ""
    }`.toUpperCase();
  };
  // if (!profile) {
  //   return (
  //     <Box>
  //       <Alert severity="warning">Profile information not available</Alert>
  //     </Box>
  //   );
  // }

  return (
    <Box>
      <Loader isLoading={isLoading} />
      <Box className="text-box-card-white m-0">
        <Box className="text-box-header">
          <h3>{t("profile.header")}</h3>
          {!isEditing && (
            <Stack direction="row" alignItems="center" gap={1}>
              <Button
                color="secondary"
                variant="contained"
                className="btn-orange btn-dark"
                onClick={() => navigate("/profile/change-password")}
              >
                <LockResetOutlinedIcon /> &nbsp;
                {t("profile.button.changePassword")}
              </Button>
              <Tooltip title={t("profile.tooltipEdit")} placement="top" arrow>
                <button
                  className="btn-nostyle icon-btn-edit"
                  onClick={handleEdit}
                >
                  <IconBtnEditBase ariaLabel={t("profile.ariaEdit")} />
                </button>
              </Tooltip>
            </Stack>
          )}
        </Box>

        <Grid container rowSpacing={1.5} columnSpacing={2.5}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" mb={3}>
              <Box
                className="username"
                sx={{
                  width: 80,
                  height: 80,
                  fontSize: "2rem",
                  mr: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "var(--auto-green)",
                  color: "var(--white)",
                  borderRadius: "50%",
                  textTransform: "uppercase",
                }}
              >
                {profile?.firstname && profile?.lastname ? (
                  getInitials(profile?.firstname, profile?.lastname)
                ) : (
                  <Person />
                )}
              </Box>
              <Box flex={1}>
                <Typography variant="h5" gutterBottom>
                  {profile?.firstname} {profile?.lastname}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  @{profile?.username}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
            <Stack sx={{ marginBottom: "20px" }}>
              <label className="label-text">
                {t("profile.label.firstName")}
                <span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="firstname"
                value={editedProfile?.firstname || ""}
                onChange={(e) => handleInputChange("firstname", e.target.value)}
                className={`form-control ${!isEditing ? "read-only" : ""}`}
                disabled={!isEditing}
                placeholder={t("profile.placeholder.firstName")}
              />
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
            <Stack sx={{ marginBottom: "20px" }}>
              <label className="label-text">
                {t("profile.label.lastName")}
                <span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="lastname"
                value={editedProfile?.lastname || ""}
                onChange={(e) => handleInputChange("lastname", e.target.value)}
                className={`form-control ${!isEditing ? "read-only" : ""}`}
                disabled={!isEditing}
                placeholder={t("profile.placeholder.lastName")}
              />
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
            <Stack sx={{ marginBottom: "20px" }}>
              <label className="label-text">
                {t("profile.label.email")}
                <span className="required-asterisk">*</span>
              </label>
              <TextField
                type="email"
                name="email"
                value={editedProfile?.email || ""}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`form-control read-only`}
                disabled
                placeholder={t("profile.placeholder.email")}
              />
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
            <Stack sx={{ marginBottom: "20px" }}>
              <label className="label-text">
                {t("profile.label.username")}
              </label>
              <TextField
                type="text"
                name="username"
                value={profile?.username || ""}
                className="form-control read-only"
                disabled
                placeholder={t("profile.placeholder.username")}
              />
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
            <Stack sx={{ marginBottom: "20px" }}>
              <label className="label-text">
                {t("profile.label.accountStatus")}
              </label>
              <TextField
                type="text"
                name="status"
                value={
                  profile?.is_active
                    ? t("profile.status.active")
                    : t("profile.status.inactive")
                }
                className="form-control read-only"
                disabled
              />
            </Stack>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
            <Stack sx={{ marginBottom: "20px" }}>
              <label className="label-text">
                {t("profile.label.createdOn")}
              </label>
              <TextField
                type="text"
                name="created_on"
                value={new Date(profile?.created_on).toLocaleDateString()}
                className="form-control read-only"
                disabled
              />
            </Stack>
          </Grid>
        </Grid>

        {isEditing && (
          <Box>
            <FlexBetween
              sx={{
                marginTop: "16px",
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <Button
                color="secondary"
                variant="contained"
                className="btn-orange"
                onClick={handleSave}
                disabled={isSaving}
              >
                <SaveOutlinedIcon /> &nbsp;{" "}
                {isSaving
                  ? t("profile.button.saving")
                  : t("profile.button.save")}
              </Button>
              <Button
                color="secondary"
                variant="contained"
                onClick={handleCancel}
                disabled={isSaving}
                className="btn-orange btn-dark"
              >
                {t("profile.button.cancel")}
              </Button>
            </FlexBetween>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Profile;
